import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { testimonials } from '../../../mocks/testimonials';

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsPerPage(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const currentTestimonials = testimonials.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-voyage-gold/10 px-6 py-3 rounded-full mb-6"
          >
            <i className="ri-star-fill text-voyage-gold"></i>
            <span className="text-night-blue font-inter text-sm font-semibold uppercase tracking-wider">Témoignages</span>
          </motion.div>

          <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-night-blue mb-4 sm:mb-6">
            Nos clients témoignent
          </h2>
          <p className="text-slate-600 font-inter text-base sm:text-lg lg:text-xl max-w-2xl mx-auto">
            Découvrez les expériences de nos clients satisfaits
          </p>

          {/* Badges de confiance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-8"
          >
            <div className="flex items-center gap-2 bg-white px-4 sm:px-6 py-3 rounded-full shadow-md">
              <i className="ri-google-fill text-2xl text-blue-600"></i>
              <div className="text-left">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <div className="text-xs text-slate-600 font-inter font-medium">4.9/5 sur Google</div>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-white px-4 sm:px-6 py-3 rounded-full shadow-md">
              <i className="ri-map-pin-fill text-2xl text-green-600"></i>
              <div className="text-left">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <div className="text-xs text-slate-600 font-inter font-medium">4.8/5 sur TripAdvisor</div>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-white px-4 sm:px-6 py-3 rounded-full shadow-md">
              <i className="ri-user-star-fill text-2xl text-voyage-gold"></i>
              <div className="text-left">
                <div className="text-sm font-inter font-bold text-night-blue">500+</div>
                <div className="text-xs text-slate-600 font-inter">Clients satisfaits</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Slider de témoignages */}
        <div className="relative">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
          >
            {currentTestimonials.map((testimonial, index) => (
              <motion.div
                key={`${currentIndex}-${index}`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all border border-slate-100 relative overflow-hidden group"
              >
                {/* Effet de fond au survol */}
                <div className="absolute inset-0 bg-gradient-to-br from-voyage-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>

                <div className="relative z-10">
                  {/* En-tête */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-voyage-gold to-amber-500 rounded-full flex items-center justify-center text-white font-playfair font-bold text-lg">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-inter font-bold text-night-blue text-base sm:text-lg">
                          {testimonial.name}
                        </h3>
                        <p className="text-slate-500 font-inter text-xs sm:text-sm">
                          {testimonial.location}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-voyage-gold/10 px-3 py-1.5 rounded-full">
                      <i className="ri-star-fill text-voyage-gold text-sm"></i>
                      <span className="font-inter font-bold text-night-blue text-sm">
                        {testimonial.rating}
                      </span>
                    </div>
                  </div>

                  {/* Étoiles */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`${
                          i < Math.floor(testimonial.rating)
                            ? 'ri-star-fill text-voyage-gold'
                            : 'ri-star-line text-slate-300'
                        } text-lg`}
                      ></i>
                    ))}
                  </div>

                  {/* Commentaire */}
                  <blockquote className="text-slate-700 font-inter text-sm sm:text-base leading-relaxed mb-4 italic">
                    "{testimonial.comment}"
                  </blockquote>

                  {/* Badge vérifié */}
                  <div className="flex items-center gap-2 text-green-600 text-xs font-inter font-medium">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span>Avis vérifié</span>
                  </div>
                </div>

                {/* Icône de citation décorative */}
                <div className="absolute top-4 right-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <i className="ri-double-quotes-r text-6xl text-voyage-gold"></i>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Contrôles du slider */}
          {totalPages > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-6 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-night-blue hover:bg-voyage-gold hover:text-white transition-all cursor-pointer z-10"
                aria-label="Témoignage précédent"
              >
                <i className="ri-arrow-left-line text-2xl"></i>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-6 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-night-blue hover:bg-voyage-gold hover:text-white transition-all cursor-pointer z-10"
                aria-label="Témoignage suivant"
              >
                <i className="ri-arrow-right-line text-2xl"></i>
              </button>
            </>
          )}
        </div>

        {/* Indicateurs de pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all cursor-pointer ${
                  currentIndex === index
                    ? 'w-8 h-3 bg-voyage-gold rounded-full'
                    : 'w-3 h-3 bg-slate-300 rounded-full hover:bg-slate-400'
                }`}
                aria-label={`Aller à la page ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* CTA pour laisser un avis */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 text-center"
        >
          <div className="bg-gradient-to-br from-night-blue to-slate-900 rounded-2xl sm:rounded-3xl p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-voyage-gold rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-voyage-gold rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative z-10">
              <h3 className="font-playfair text-3xl sm:text-4xl font-bold text-white mb-4">
                Vous avez voyagé avec nous ?
              </h3>
              <p className="text-slate-300 font-inter text-base sm:text-lg mb-8 max-w-2xl mx-auto">
                Partagez votre expérience et aidez d'autres voyageurs à faire le bon choix
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-voyage-gold text-night-blue px-8 py-4 rounded-full font-inter font-bold text-base sm:text-lg hover:bg-amber-500 transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-3 shadow-xl"
              >
                <i className="ri-star-line text-xl"></i>
                Laisser un avis
                <i className="ri-arrow-right-line"></i>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
